import React, { useState, useEffect, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { CheckCircle, FileText, Home, Printer, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { printReceiptHtml, printEquipmentPassport, hasTrialLens, TRIAL_LENS_EXTRA_DAYS } from '../../utils/receiptDownload';
import { useBranding } from '../../contexts/BrandingContext';
import { clearSalesFlowSession } from '../../utils/salesSession';
import { lensDisplayName, frameDisplayName } from '../../utils/itemDisplayName';
import QRCode from 'react-qr-code';
import i18n from '../../i18n';
import { pairStepData } from '../../utils/salePairs';

// The page sits inside SalesLayout, under a ~130px sticky header (top bar +
// step bar) and 2rem of ContentArea padding. Sizing off that keeps the card
// inside the viewport instead of forcing the whole page to scroll.
const SHELL_OFFSET = '13rem';

const Container = styled.div`
  min-height: calc(100vh - ${SHELL_OFFSET});
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Card = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  box-shadow: ${props => props.theme.shadows.xl};
  border: 1px solid ${props => props.theme.colors.border};
  width: 100%;
  max-width: 1080px;
  overflow: hidden;
  animation: slideUp 0.4s ease-out;

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const LoadingCard = styled(Card)`
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing['2xl']};
  color: ${props => props.theme.colors.textSecondary};
`;

const Hero = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xl};
  background: linear-gradient(135deg, rgba(22, 163, 74, 0.1), rgba(37, 99, 235, 0.06));
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const SuccessIcon = styled.div`
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${props => props.theme.colors.success};
  color: ${props => props.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3);
`;

const HeroText = styled.div`
  min-width: 0;
  flex: 1;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const OrderNumberTag = styled.div`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 0.2rem;

  span {
    font-weight: ${props => props.theme.fontWeights.semibold};
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const StatusPill = styled.span`
  flex-shrink: 0;
  align-self: center;
  padding: 0.3rem 0.85rem;
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.success};
  color: ${props => props.theme.colors.successDark};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.semibold};
  text-transform: capitalize;
  white-space: nowrap;

  @media (max-width: 560px) {
    display: none;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xl};
`;

// Two rows on the left (details, then its actions) and the client card on
// the right SPANNING both of those rows — so the card's bottom edge lines up
// with the action row's bottom edge instead of floating next to just the top
// row with a gap of nothing beside the buttons.
const ContentRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: auto auto;
  grid-template-areas:
    "details card"
    "actions card";
  gap: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      "details"
      "actions"
      "card";
  }
`;

const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${props => props.theme.spacing.sm};

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

// Rest-to-pay and the urgent delivery states are solid fills (not tints) so
// they're unmissable at a glance, not something you have to read the label to
// notice. Each solid tone maps to a {bg, border} pair pulled from the theme:
//   due          — balance still owed             → red
//   deliverySame — lens ships same day             → red  (most urgent)
//   deliverySoon — ordered from external stock     → orange
//   deliveryRx   — lab-made / 7+ days              → purple
const SOLID_TONE_COLORS = {
  due: (theme) => ({ bg: theme.colors.danger, border: theme.colors.dangerDark }),
  deliverySame: (theme) => ({ bg: theme.colors.danger, border: theme.colors.dangerDark }),
  deliverySoon: (theme) => ({ bg: theme.colors.warning, border: theme.colors.warningDark }),
  deliveryRx: (theme) => ({ bg: theme.colors.secondary, border: theme.colors.secondaryDark }),
};

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

const StatTile = styled.div`
  min-width: 0;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.lg};
  background: ${props => SOLID_TONE_COLORS[props.$tone]
    ? SOLID_TONE_COLORS[props.$tone](props.theme).bg
    : props.$tone === 'paid'
      ? 'rgba(22, 163, 74, 0.08)'
      : props.theme.colors.gray50};
  border: 1px solid ${props => SOLID_TONE_COLORS[props.$tone]
    ? SOLID_TONE_COLORS[props.$tone](props.theme).border
    : props.$tone === 'paid'
      ? 'rgba(22, 163, 74, 0.25)'
      : props.theme.colors.border};
  ${props => props.$blink && css`
    animation: ${blink} 1.4s ease-in-out infinite;
  `}
`;

const StatLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => SOLID_TONE_COLORS[props.$tone]
    ? 'rgba(255, 255, 255, 0.85)'
    : props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const StatValue = styled.div`
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => SOLID_TONE_COLORS[props.$tone]
    ? props.theme.colors.white
    : props.$tone === 'paid'
      ? props.theme.colors.successDark
      : props.theme.colors.textPrimary};
  word-break: break-word;
`;

const OrderDetails = styled.div`
  grid-area: details;
  background: ${props => props.theme.colors.gray50};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.lg};
  text-align: left;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

// A hairline between fields (instead of a plain gap) reads as one organized
// strip of facts rather than three separately-drifting blocks.
const DetailBlock = styled.div`
  min-width: 0;
  padding: 0 ${props => props.theme.spacing.md};

  &:first-child {
    padding-left: 0;
  }

  &:not(:last-child) {
    border-right: 1px solid ${props => props.theme.colors.border};
  }

  @media (max-width: 1024px) {
    padding-top: ${props => props.theme.spacing.sm};
    padding-bottom: ${props => props.theme.spacing.sm};

    &:nth-child(2n) {
      border-right: none;
    }

    &:nth-child(-n+2) {
      padding-top: 0;
    }
  }

  @media (max-width: 560px) {
    border-right: none !important;
    padding: ${props => props.theme.spacing.sm} 0;

    &:first-child {
      padding-top: 0;
    }
  }
`;

const DetailBlockLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 0.1rem;
`;

const DetailBlockValue = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.semibold};
  word-break: break-word;
  line-height: 1.35;
`;

// Spans both grid rows (details+actions on the left) so its own bottom edge
// lines up with the action row's bottom instead of ending after just the
// details row. The header stays pinned to the top (like the details/action
// cards it's aligned with); only the QR box centers in the leftover height,
// via QRCodeBoxWrap below — centering the header+QR as one unit would push
// the header down away from the card's top edge.
const QRCodeContainer = styled.div`
  grid-area: card;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  background: ${props => props.theme.colors.white};
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
`;

const QRCodeBoxWrap = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Caption on the left, the print action as a small icon (no label) on the
// right — replaces the separate full-width "Print client card" button, which
// only made sense back when actions all lived in one bottom row.
const QRCodeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  padding: 0;
  background: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.textSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.gray100};
    color: ${props => props.theme.colors.textPrimary};
    border-color: ${props => props.theme.colors.borderDark};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const QRCodeBox = styled.div`
  display: inline-flex;
  padding: 0.4rem;
  background: #fff;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
`;

const QRCodeCaption = styled.div`
  font-size: 0.65rem;
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

// Single full-width row instead of a stacked side column — collapses what
// used to be ~200px of vertical button stack down to one ~45px row, which is
// most of what was leaving dead space at the bottom of the card.
const ActionBar = styled.div`
  grid-area: actions;
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.sm};

  /* Scoped to ActionBar so a Button used standalone elsewhere (e.g. the
     client card's print icon) doesn't inherit flex-grow — in a vertical flex
     flow that grow would stretch it to fill all leftover height instead of
     sizing to its own content. */
  & > button {
    flex: 1 1 160px;
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background: ${props => props.$primary ? props.theme.colors.primary : props.theme.colors.white};
  color: ${props => props.$primary ? props.theme.colors.white : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.$primary ? 'transparent' : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  line-height: 1.2;
  text-align: center;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => props.$primary ? props.theme.colors.primaryDark : props.theme.colors.gray100};
    border-color: ${props => props.$primary ? 'transparent' : props.theme.colors.borderDark};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const formatMoney = (n) => {
  if (n == null || n === '' || Number.isNaN(Number(n))) return '—';
  return `${Number(n).toLocaleString()} DZD`;
};

const fullName = (person) => {
  if (!person || typeof person !== 'object') return '';
  return [person.firstName || person.first_name, person.lastName || person.last_name].filter(Boolean).join(' ').trim();
};

// The order payload comes back in different shapes from POST/PUT/PUT-status,
// and items may be stored under several keys. Fall back to raw_data.items and
// sessionStorage so display never blanks out on stale or partial responses.
const collectOrderItems = (order) => {
  const candidates = [
    order?.items,
    order?.order_items,
    order?.orderItems,
    order?.raw_data?.items,
  ];
  for (const list of candidates) {
    if (Array.isArray(list) && list.length) return list;
  }
  try {
    const summary = JSON.parse(sessionStorage.getItem('orderSummary') || '{}');
    if (Array.isArray(summary.items) && summary.items.length) return summary.items;
  } catch (_) { /* ignore */ }
  return [];
};

const findItemName = (order, type) => {
  const items = collectOrderItems(order);
  // A two-pair sale names both pairs' frames and both lenses, each with its pair.
  if (items.some((i) => i.specifications?.visionPair)) {
    const lines = items.filter((i) => (i.type || i.item_type) === type);
    if (lines.length) {
      const pairOf = (i) => (i.specifications?.visionPair
        ? ` (${i18n.t(i.specifications.visionPair === 'near' ? 'pairs.pairNear' : 'pairs.pairFar')})` : '');
      return lines.map((i) => (type === 'lens'
        ? lensDisplayName(i)
        : [frameDisplayName(i.name), i.brand, i.model].filter(Boolean).join(' · ')) + pairOf(i)).join(' + ');
    }
  }
  // A customer can leave with more than one frame (spares/backups bought
  // alongside the prescription pair) — every frame-typed line still gets its
  // own stock deduction, but this tile only had room for one name. Show the
  // first and note how many more rather than silently dropping the rest of
  // what the customer paid for.
  if (type === 'frame') {
    const frameItems = items.filter((i) => (i.type || i.item_type) === 'frame');
    if (frameItems.length > 1) {
      const [first, ...rest] = frameItems;
      return `${[frameDisplayName(first.name), first.brand, first.model].filter(Boolean).join(' · ')} (+${rest.length})`;
    }
  }
  const item = items.find((i) => (i.type || i.item_type) === type);
  // Lenses show the store's commercial name, never the supplier/real one.
  if (type === 'lens' && item) return lensDisplayName(item);
  if (item) return [frameDisplayName(item.name), item.brand, item.model].filter(Boolean).join(' · ');
  if (type === 'frame') {
    try {
      const sel = JSON.parse(sessionStorage.getItem('selectedFrame') || '{}');
      // Module-level helper: no hook here, so read from the i18n instance.
      if (sel.hasOwnFrame) return i18n.t('common.clientsOwnFrame');
      if (sel.name) return [sel.name, sel.brand, sel.model].filter(Boolean).join(' · ');
    } catch (_) { /* ignore */ }
  }
  if (type === 'lens') {
    try {
      const sel = JSON.parse(sessionStorage.getItem('selectedLens') || 'null');
      if (sel?.commercialName || sel?.name) return sel.commercialName || sel.name;
    } catch (_) { /* ignore */ }
  }
  return '—';
};

// Longest lead time declared by any item, or by the lens picked this session
// as a last resort. Returns null (not 0) when nothing declares a lead time, so
// callers can tell "no data" apart from "same day".
const deliveryDaysFromItems = (order) => {
  const items = collectOrderItems(order);
  const days = items.reduce((max, item) => {
    const value = Number(
      item?.specifications?.estimatedDays
      ?? item?.estimatedDays
      ?? item?.specifications?.defaultDeliveryDaysMax
      ?? 0
    );
    return Number.isFinite(value) ? Math.max(max, value) : max;
  }, 0);
  // Trial lenses (progressive, ticked on the prescription) add 10 days — same
  // count the passport and receipt print.
  const extra = hasTrialLens(order) ? TRIAL_LENS_EXTRA_DAYS : 0;
  if (days > 0) return days + extra;
  try {
    const sel = JSON.parse(sessionStorage.getItem('selectedLens') || 'null');
    const lensDays = Number(sel?.estimatedDays || 0);
    if (lensDays > 0) return lensDays + extra;
  } catch (_) { /* ignore */ }
  return extra || null;
};

const estimatedDelivery = (order) => {
  if (order?.estimatedCompletion) return new Date(order.estimatedCompletion).toLocaleDateString();
  const days = deliveryDaysFromItems(order);
  if (days != null) return i18n.t('common.dayCount', { count: days });
  try {
    const sel = JSON.parse(sessionStorage.getItem('selectedLens') || 'null');
    if (sel?.defaultDeliveryLabel) return sel.defaultDeliveryLabel;
  } catch (_) { /* ignore */ }
  return i18n.t('common.defaultDeliveryWindow');
};

// Numeric lead time used only to pick the delivery tile's color/blink — when an
// explicit completion date is set (rather than a day count), the days are
// derived from the gap between sale and completion so the same thresholds apply.
const resolveDeliveryDays = (order) => {
  if (order?.estimatedCompletion) {
    const start = new Date(order.orderDate || order.createdAt || Date.now());
    const end = new Date(order.estimatedCompletion);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      return Math.max(0, Math.round((end - start) / 86400000));
    }
  }
  return deliveryDaysFromItems(order);
};

// Mirrors the lens lead-time buckets used server-side (deliveryForDispo in
// supabaseCommerce.js): 0 = same-day stock, 1–6 = ordered from external stock
// (typically 3), 7+ = RX / lab-made. Unknown lead time stays neutral — no
// color, no blink, since there's nothing urgent to signal.
const RX_DAYS_THRESHOLD = 7;
const classifyDeliveryTone = (order) => {
  const days = resolveDeliveryDays(order);
  if (days == null) return null;
  if (days <= 0) return 'deliverySame';
  if (days < RX_DAYS_THRESHOLD) return 'deliverySoon';
  return 'deliveryRx';
};

const OrderConfirmation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  // The client's own QR identity (not the order's) — printed on a label so the
  // client can be pulled up later by scan, or by typing the code by hand.
  const [clientCode, setClientCode] = useState(null);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const createOrder = async () => {
      try {
        // Check if order already exists in sessionStorage
        // If it does, we might need to update it with measurements if they are missing
        const existingOrderJson = sessionStorage.getItem('orderData');
        let existingOrder = null;

        if (existingOrderJson) {
          try {
            existingOrder = JSON.parse(existingOrderJson);
            // If we have an existing order, we don't return immediately anymore.
            // We proceed to gather data and UPDATE the order instead of creating a new one if needed.
            console.log('Found existing order, checking for updates:', existingOrder);
          } catch (e) {
            console.error('Error parsing existing order:', e);
          }
        }

        let visitorId = sessionStorage.getItem('visitorId');
        const prescriptionId = sessionStorage.getItem('prescriptionId');

        if (!visitorId) {
          throw new Error('No visitor ID found');
        }

        // Get order data from sessionStorage
        const orderSummary = JSON.parse(sessionStorage.getItem('orderSummary') || '{}');
        const paymentData = JSON.parse(sessionStorage.getItem('paymentData') || '{}');
        const selectedFrame = JSON.parse(sessionStorage.getItem('selectedFrame') || '{}');

        // Optical adjustments from the Frame selection step (numeric values + toggles)
        const frameSelectionData = JSON.parse(sessionStorage.getItem('frameSelectionData') || '{}');
        const frameAdjustmentsVerified = {
          ...(frameSelectionData.adjustmentValues || {}),
          ...(frameSelectionData.adjustmentChecks || {})
        };
        // Store chosen at the start of the sale (admins/managers). Regular sales
        // staff have no salesStore, so the server falls back to their own store.
        let salesStoreId;
        try { salesStoreId = JSON.parse(sessionStorage.getItem('salesStore') || 'null')?.id; } catch (_) { salesStoreId = undefined; }

        const rawMeasurementData = JSON.parse(sessionStorage.getItem('measurementFormData') || '{}');
        console.log('Raw measurementFormData from session:', rawMeasurementData);

        // mountingHeight: drafts from before mounting height was split per eye.
        const MEASUREMENT_KEYS = ['mountingHeightOD', 'mountingHeightOG', 'mountingHeight', 'pdOD', 'pdOG'];

        // Sanitize measurements: only current fields, numeric
        const sanitizeMeasurements = (data) => {
          const clean = {};
          MEASUREMENT_KEYS.forEach((key) => {
            const val = data[key];
            if (val !== '' && val !== null && val !== undefined && !isNaN(Number(val))) {
              clean[key] = Number(val);
            }
          });
          return clean;
        };

        const measurementData = sanitizeMeasurements(rawMeasurementData);
        console.log('Sanitized measurementData for payload:', measurementData);

        // Determine payment status based on deposit
        let paymentStatus = 'pending';
        if (paymentData.deposit >= orderSummary.total) {
          paymentStatus = 'paid';
        } else if (paymentData.deposit > 0) {
          paymentStatus = 'partial';
        }

        // Get auth token from localStorage
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found. Please log in again.');
        }

        let response;

        const createVisitorFromSession = async () => {
          const visitorData = JSON.parse(sessionStorage.getItem('visitorData') || '{}');
          if (!visitorData.gender || !visitorData.ageGroup) {
            throw new Error('Visitor not found and no visit data is available to recreate it.');
          }
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const recreated = await axios.post('/api/visitors', {
            ...visitorData,
            timeTaken: Number(sessionStorage.getItem('visitorTimeTaken') || 0),
            salesPerson: user.id || null,
          }, {
            headers: { Authorization: `Bearer ${token}` },
          });
          visitorId = recreated.data._id;
          sessionStorage.setItem('visitorId', visitorId);
          return visitorId;
        };

        const submitOrder = async (payload) => axios.post('/api/orders', payload, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        // A two-pair sale is one order: pair 1's (far) measures and frame
        // adjustments stay where every screen reads them; pair 2's (close
        // up) ride along under `near`.
        if (Array.isArray(orderSummary.pairs) && orderSummary.pairs.length === 2) {
          const near = pairStepData('near');
          const nearAdjustments = {
            ...(near.frameSelectionData?.adjustmentValues || {}),
            ...(near.frameSelectionData?.adjustmentChecks || {}),
          };
          measurementData.near = sanitizeMeasurements(near.measurementFormData || {});
          frameAdjustmentsVerified.near = nearAdjustments;
        }

        // A remembered order that was cancelled since can't be reused: the
        // server refuses it and this sale is saved as a new order instead.
        let reuseExisting = !!(existingOrder && existingOrder._id);
        if (reuseExisting) {
          // UDPATE existing order
          console.log('Updating existing order with measurements and payment...');

          const updateData = {
            // Promote the Rx-step draft out of `pending`. The lens was held the
            // moment it was picked; this is what converts that hold into a real
            // stock deduction. Leaving it pending would also let the standby
            // sweep cancel this order and release its lens.
            status: 'confirmed',
            measurements: measurementData,
            frameAdjustmentsVerified,
            ...(salesStoreId ? { store: salesStoreId } : {}),
            payment: {
              method: paymentData.method || 'cash',
              status: paymentStatus,
              amount: paymentData.deposit || 0
            },
            salesPassesOrderUnderResponsibility: Boolean(
              paymentData.salesPassesOrderUnderResponsibility
            ),
            // Update items and totals with the final calculation from Payment.js
            items: orderSummary.items,
            subtotal: orderSummary.subtotal,
            tax: orderSummary.tax,
            discount: orderSummary.discount,
            total: orderSummary.total
          };

          try {
            response = await axios.put(`/api/orders/${existingOrder._id}`, updateData, {
              headers: { Authorization: `Bearer ${token}` }
            });
            console.log('Order updated successfully:', response.data);
          } catch (updateErr) {
            if (updateErr.response?.data?.code !== 'ORDER_CANCELLED') throw updateErr;
            ['orderId', 'orderNumber', 'orderData'].forEach((k) => sessionStorage.removeItem(k));
            reuseExisting = false;
          }
        }

        if (!reuseExisting) {
          // CREATE new order
          console.log('Creating new order...');
          const orderData = {
            visitor: visitorId,
            prescription: prescriptionId || null,
            measurements: measurementData,
            frameAdjustmentsVerified,
            ...(salesStoreId ? { store: salesStoreId } : {}),
            items: orderSummary.items || [
              {
                type: 'frame',
                name: selectedFrame.name || 'Frame',
                brand: selectedFrame.brand || 'Optiva',
                model: selectedFrame.model || '',
                material: selectedFrame.material || '',
                color: selectedFrame.color || '',
                quantity: 1,
                unitPrice: selectedFrame.price || 25000,
                totalPrice: selectedFrame.price || 25000
              },
              {
                type: 'lens',
                name: 'Prescription Lenses',
                brand: 'Optiva',
                material: 'Polycarbonate',
                quantity: 1,
                unitPrice: 35000,
                totalPrice: 35000
              },
              {
                type: 'coating',
                name: 'Anti-Reflective Coating',
                brand: 'Optiva',
                quantity: 1,
                unitPrice: 8000,
                totalPrice: 8000
              }
            ],
            subtotal: orderSummary.subtotal || 68000,
            tax: orderSummary.tax || 6800,
            discount: orderSummary.discount || 0,
            total: orderSummary.total || 74800,
            payment: {
              method: paymentData.method || 'cash',
              status: paymentStatus,
              amount: paymentData.deposit || 0
            },
            salesPassesOrderUnderResponsibility: Boolean(
              paymentData.salesPassesOrderUnderResponsibility
            )
          };

          try {
            response = await submitOrder(orderData);
          } catch (createError) {
            if (createError.response?.status === 404 && createError.response?.data?.message === 'Visitor not found') {
              const nextVisitorId = await createVisitorFromSession();
              response = await submitOrder({ ...orderData, visitor: nextVisitorId });
            } else {
              throw createError;
            }
          }
          console.log('Order created successfully:', response.data);
        }

        // Update order status to completed (if valid to do so)
        // Only if it's not already completed/confirmed
        if (response.data.status === 'pending' || response.data.status === 'order_created') {
          try {
            const statusResponse = await axios.put(`/api/orders/${response.data._id}/status`, {
              status: 'confirmed', // Changed to confirmed instead of completed for Technician flow
              notes: 'Order confirmed and ready for processing'
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            response.data = statusResponse.data?.order || {
              ...response.data,
              status: 'confirmed',
            };
          } catch (updateError) {
            console.error('Error updating order status:', updateError);
          }
        }

        // Store order in sessionStorage to prevent duplicate creation on refresh
        sessionStorage.setItem('orderData', JSON.stringify(response.data));
        sessionStorage.setItem('orderId', response.data._id);
        sessionStorage.setItem('orderNumber', response.data.orderNumber);

        setOrderData(response.data);
        console.log('Order created successfully:', response.data);
      } catch (error) {
        console.error('Error creating order:', error);
        console.error('Error response:', error.response);
        console.error('Error details:', error.response?.data || error.message);

        // Show detailed error to user
        const errorMessage = error.response?.data?.error
          || error.response?.data?.message
          || error.message
          || 'Unknown error';
        const errorDetails = error.response?.data?.errors
          ? JSON.stringify(error.response.data.errors)
          : '';

        alert(`Failed to create order: ${errorMessage}${errorDetails ? '\n' + errorDetails : ''}`);


      } finally {
        setLoading(false);
      }
    };

    createOrder();
  }, []);

  // The code is derived server-side from the client's identity, so it is the
  // same on every visit — a label printed today still resolves next year.
  useEffect(() => {
    const visitorId = orderData?.visitor?.id || orderData?.visitor?._id || orderData?.visitor
      || sessionStorage.getItem('visitorId');
    if (!visitorId || typeof visitorId !== 'string') return;
    const token = localStorage.getItem('token');
    let cancelled = false;
    axios.get(`/api/visitors/${visitorId}/client-code`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => { if (!cancelled) setClientCode(res.data); })
      .catch((err) => console.error('Could not load client code:', err));
    return () => { cancelled = true; };
  }, [orderData]);

  const readVisitorData = () => {
    let visitorData = {};
    try { visitorData = JSON.parse(sessionStorage.getItem('visitorData') || '{}'); } catch { visitorData = {}; }
    if (!visitorData.birthday && !visitorData.patientBirthday) {
      try {
        const pfd = JSON.parse(sessionStorage.getItem('prescriptionFormData') || '{}');
        if (pfd.patientBirthday) visitorData = { ...visitorData, patientBirthday: pfd.patientBirthday };
      } catch (_) {}
    }
    return visitorData;
  };

  const readPaymentData = () => {
    let paymentData = {};
    try {
      paymentData = JSON.parse(sessionStorage.getItem('paymentData') || '{}');
    } catch {
      paymentData = {};
    }
    return paymentData;
  };

  const handlePrintReceipt = (copyType) => {
    if (!orderData) return;
    printReceiptHtml({
      orderData,
      paymentData: readPaymentData(),
      visitorData: readVisitorData(),
      copyType,
      branding,
    });
  };

  const handlePrintPassport = () => {
    if (!orderData) return;
    printEquipmentPassport({
      orderData,
      paymentData: readPaymentData(),
      visitorData: readVisitorData(),
      branding,
    });
  };

  // Reuses the same dedicated label print page as the frame stickers, so the
  // client card comes out at the identical physical size and prints the same
  // in every browser.
  const handlePrintClientCard = () => {
    if (!clientCode?.code) return;
    const url = `/print-label/${encodeURIComponent(clientCode.code)}?name=${encodeURIComponent(customerName)}`;
    const win = window.open(url, 'client-label-print', 'width=900,height=700,left=200,top=80');
    if (!win) window.alert('Please allow popups to print the client card.');
  };

  const customerName = fullName(orderData?.visitor) || (() => {
    try {
      const visitorData = JSON.parse(sessionStorage.getItem('visitorData') || '{}');
      return fullName(visitorData) || visitorData.name || '—';
    } catch {
      return '—';
    }
  })();

  const paidAmount = Number(orderData?.payment?.amount || 0);
  const restToPay = Math.max(0, Number(orderData?.total || 0) - paidAmount);
  const deliveryTone = orderData ? classifyDeliveryTone(orderData) : null;

  if (loading) {
    return (
      <Container>
        <LoadingCard>
          <LoadingSpinner />
          <p>{t('orderConfirmation.creating')}</p>
        </LoadingCard>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Hero>
          <SuccessIcon>
            <CheckCircle size={28} />
          </SuccessIcon>
          <HeroText>
            <Title>{t('orderConfirmation.confirmed')}</Title>
            {orderData?.orderNumber && (
              <OrderNumberTag>
                {t('orderConfirmation.orderNumber')}: <span>{orderData.orderNumber}</span>
              </OrderNumberTag>
            )}
          </HeroText>
          <StatusPill>{orderData?.status || t('orderConfirmation.statusDefault')}</StatusPill>
        </Hero>

        <Body>
          {orderData && (
            <>
              <StatRow>
                <StatTile $tone="paid">
                  <StatLabel>{t('orderConfirmation.paid')}</StatLabel>
                  <StatValue $tone="paid">{formatMoney(paidAmount)}</StatValue>
                </StatTile>
                <StatTile $tone={restToPay > 0 ? 'due' : 'neutral'}>
                  <StatLabel $tone={restToPay > 0 ? 'due' : 'neutral'}>{t('orderConfirmation.restToPay')}</StatLabel>
                  <StatValue $tone={restToPay > 0 ? 'due' : 'neutral'}>{formatMoney(restToPay)}</StatValue>
                </StatTile>
                <StatTile $tone={deliveryTone} $blink={!!deliveryTone}>
                  <StatLabel $tone={deliveryTone}>{t('orderConfirmation.timeToDeliver')}</StatLabel>
                  <StatValue $tone={deliveryTone}>{estimatedDelivery(orderData)}</StatValue>
                </StatTile>
              </StatRow>

              <ContentRow>
                <OrderDetails>
                  <DetailsGrid>
                    <DetailBlock>
                      <DetailBlockLabel>{t('orderConfirmation.customer')}</DetailBlockLabel>
                      <DetailBlockValue>{customerName}</DetailBlockValue>
                    </DetailBlock>
                    <DetailBlock>
                      <DetailBlockLabel>{t('orderConfirmation.frame')}</DetailBlockLabel>
                      <DetailBlockValue>{findItemName(orderData, 'frame')}</DetailBlockValue>
                    </DetailBlock>
                    <DetailBlock>
                      <DetailBlockLabel>{t('orderConfirmation.lens')}</DetailBlockLabel>
                      <DetailBlockValue>{findItemName(orderData, 'lens')}</DetailBlockValue>
                    </DetailBlock>
                  </DetailsGrid>
                </OrderDetails>

                <ActionBar>
                  <Button onClick={() => handlePrintReceipt('customer')}>
                    <Printer size={16} />
                    {t('orderConfirmation.customerCopy')}
                  </Button>
                  {/* Same invoice the manager prints from the Orders tab. */}
                  <Button onClick={() => handlePrintReceipt('invoice')}>
                    <FileText size={16} />
                    {t('manager.invoice')}
                  </Button>
                  <Button onClick={handlePrintPassport}>
                    <Truck size={16} />
                    {t('orderConfirmation.equipmentPassport')}
                  </Button>
                  <Button $primary onClick={() => {
                    // Use the shared key list rather than a local one — the hand
                    // written version here was missing visitorFormData (plus
                    // visitorTimeTaken / returningClientData / previousPrescription
                    // / salesStore), so the next visit opened with the previous
                    // client's gender, age group and motive still selected.
                    clearSalesFlowSession();
                    navigate('/visitor-entry');
                  }}>
                    <Home size={16} />
                    {t('orderConfirmation.newOrder')}
                  </Button>
                </ActionBar>

                {clientCode?.code && (
                  <QRCodeContainer>
                    <QRCodeHeader>
                      <QRCodeCaption>{t('orderConfirmation.clientCard')}</QRCodeCaption>
                      <IconButton
                        onClick={handlePrintClientCard}
                        title={t('orderConfirmation.printClientCard')}
                        aria-label={t('orderConfirmation.printClientCard')}
                      >
                        <Printer size={14} />
                      </IconButton>
                    </QRCodeHeader>
                    <QRCodeBoxWrap>
                      <QRCodeBox>
                        <QRCode value={clientCode.code} size={64} />
                      </QRCodeBox>
                    </QRCodeBoxWrap>
                  </QRCodeContainer>
                )}
              </ContentRow>
            </>
          )}
        </Body>
      </Card>
    </Container>
  );
};

export default OrderConfirmation;
